<script lang="ts">
	import { resolve } from '$app/paths';
	import { page } from '$app/state';
	import Fa from 'svelte-fa';
	import { slide } from 'svelte/transition';
	import SettingsNavFlyout from './SettingsNavFlyout.svelte';
	import SideRailAnchor from '$components/SideRailAnchor.svelte';
	import { closeDrawer } from '$lib/shared/lib/drawerStore';
	import {
		faGear,
		faPodcast,
		faLayerGroup,
		faCommentDots,
		faMicrophone,
		faImage,
		faBullseye,
		faCreditCard,
		faChevronDown,
		faChevronRight
	} from '@fortawesome/free-solid-svg-icons';

	interface Props {
		tag: string;
		collapsed?: boolean;
		selectedPath?: string | null;
	}

	let { tag, collapsed = false, selectedPath = null }: Props = $props();

	let expanded = $state(false);
	let manuallyCollapsed = $state(false);

	const settingsSections = [
		{ title: 'Show', path: 'show', icon: faPodcast },
		{ title: 'Overlay', path: 'overlay', icon: faLayerGroup },
		{ title: 'Text Tips', path: 'text-tips', icon: faCommentDots },
		{ title: 'Voice Tips', path: 'voice-tips', icon: faMicrophone },
		{ title: 'Image Tips', path: 'image-tips', icon: faImage },
		{ title: 'Goals', path: 'goals', icon: faBullseye },
		{ title: 'Payments', path: 'payments', icon: faCreditCard }
	];

	let isOnSettingsRoute = $derived(page.route.id?.startsWith('/admin/[tag]/settings/') ?? false);
	let settingsSubPath = $derived(
		isOnSettingsRoute ? (page.route.id!.split('/').filter((x) => x)[3] ?? null) : null
	);

	$effect(() => {
		if (isOnSettingsRoute && !expanded && !manuallyCollapsed) {
			expanded = true;
		}
	});
	$effect(() => {
		if (!isOnSettingsRoute) {
			expanded = false;
			manuallyCollapsed = false;
		}
	});
</script>

{#if collapsed}
	<SettingsNavFlyout {tag} {selectedPath} />
{:else}
	<!-- Settings parent row — uses AppRailAnchor to match Panel/Moderation alignment -->
	<div class="relative">
		<SideRailAnchor
			href="/admin/{tag}/settings"
			selected={selectedPath === 'settings'}
			onclose={() => {
				closeDrawer();
				expanded = true;
				manuallyCollapsed = false;
			}}
		>
			{#snippet lead()}
				<div class="flex space-x-4">
					<div class="mt-1 justify-center"><Fa icon={faGear} /></div>
					<div>Settings</div>
				</div>
			{/snippet}
		</SideRailAnchor>
		<!-- Chevron toggle overlaid on the right side (does NOT navigate) -->
		<button
			class="absolute top-0 right-0 flex h-full items-center px-3 transition-colors hover:text-primary-500"
			onclick={(e) => {
				e.preventDefault();
				e.stopPropagation();
				if (expanded) {
					expanded = false;
					manuallyCollapsed = true;
				} else {
					expanded = true;
					manuallyCollapsed = false;
				}
			}}
		>
			<Fa icon={expanded ? faChevronDown : faChevronRight} size="xs" />
		</button>
	</div>

	<!-- Sub-items with slide transition -->
	{#if expanded}
		<div transition:slide={{ duration: 200 }} class="pb-3">
			{#each settingsSections as section, _i (_i)}
				<a
					href={resolve(`/admin/${tag}/settings/${section.path}`)}
					class="flex items-center space-x-3 py-1.5 pl-10 text-sm transition-colors hover:text-primary-500
						{settingsSubPath === section.path ? 'bg-surface-50-800 text-primary-500' : ''}"
					onclick={closeDrawer}
				>
					<Fa icon={section.icon} size="sm" />
					<span>{section.title}</span>
				</a>
			{/each}
		</div>
	{/if}
{/if}
